<template>
  <div>
    <b-modal
      id="terms-of-service-modal"
      title="Terms of Service"
      size="lg"
      scrollable
      ok-only
      ok-title="Close"
      ok-variant="secondary"
    >
      <div class="tos-content">
        <p class="text-muted small">
          Last updated: February 2026
        </p>

        <h6>1. Acceptance of Terms</h6>
        <p>
          By accessing or using the IFE Data platform ("the Service"), you agree to be bound by these
          Terms of Service ("Terms"). If you are accessing the Service on behalf of a company or other
          legal entity, you represent that you have the authority to bind that entity to these Terms.
          If you do not agree to these Terms, you may not access or use the Service.
        </p>
        <p>
          Violation of these Terms may result in suspension or termination of access in accordance
          with clause 9.
        </p>

        <h6>2. Access and Authorisation</h6>
        <p>
          Access to the Service is granted solely to authorised users of organisations that have
          entered into a separate agreement with Invitial. Your access credentials are personal and
          must not be shared with any third party. You are responsible for all activity that occurs
          under your account. You must notify Invitial immediately of any unauthorised use of your
          account.
        </p>

        <h6>3. Permitted Use</h6>
        <p>
          You may use the Service only for lawful business purposes in connection with managing
          in-flight entertainment content data. Any breach of the restrictions below may result in
          immediate suspension or termination of access under clause 9. You agree not to:
        </p>
        <ul>
          <li>use the Service in any manner that could damage, disable, or impair the Service;</li>
          <li>attempt to gain unauthorised access to any part of the Service or its related systems;</li>
          <li>
            scrape, copy, or otherwise extract data from the Service beyond what is required for
            your authorised business use;
          </li>
          <li>use the Service to transmit any unlawful, harmful, or objectionable content; or</li>
          <li>circumvent or interfere with any security or access control features of the Service.</li>
        </ul>

        <h6>4. Confidentiality</h6>
        <p>
          The data, content, and information accessible through the Service may be commercially
          sensitive and confidential. You agree to treat all information obtained through the Service
          as confidential and not to disclose it to any third party without the prior written consent
          of Invitial, except:
        </p>
        <ul>
          <li>as required by applicable law or regulation;</li>
          <li>
            to your employees, contractors, or professional advisers who have a genuine need to know
            and are themselves bound by equivalent confidentiality obligations; or
          </li>
          <li>
            to the extent that the information is or becomes publicly available through no fault of
            your own.
          </li>
        </ul>

        <h6>5. Intellectual Property</h6>
        <p>
          All intellectual property rights in the Service, including its software, design, and
          content, are owned by or licensed to Invitial. Nothing in these Terms grants you any rights
          to use Invitial's trademarks, logos, or other proprietary materials. Content data entered
          into the Service by your organisation remains the property of your organisation.
        </p>

        <h6>6. Data and Privacy</h6>
        <p>
          Invitial processes personal data in accordance with applicable data protection legislation,
          including the UK GDPR and the Data Protection Act 2018. By using the Service, you
          acknowledge that Invitial will process personal data (including your name, email address,
          and usage data) as necessary to provide the Service and as described in our Privacy Policy,
          which is available alongside the link to these Terms. Invitial implements appropriate
          technical and organisational measures to protect the security of data held within the
          Service.
        </p>

        <h6>7. Availability</h6>
        <p>
          Invitial will use reasonable endeavours to make the Service available on a substantially
          continuous basis, but does not guarantee uninterrupted or error-free access. The Service
          may be subject to scheduled or emergency maintenance. Invitial will endeavour to provide
          at least 48 hours advance notice of planned maintenance where practicable.
        </p>
        <p>
          In the event of any outage or material degradation of the Service, Invitial will use
          reasonable efforts to restore normal access as soon as practicable and will keep affected
          users informed.
        </p>

        <h6>8. Limitation of Liability</h6>
        <p>
          Nothing in these Terms excludes or limits Invitial's liability for: (a) death or personal
          injury caused by Invitial's negligence; (b) fraud or fraudulent misrepresentation; or
          (c) any other liability that cannot be excluded or limited under applicable law.
        </p>
        <p>
          Subject to the above, and to the fullest extent permitted by applicable law, Invitial shall
          not be liable for any indirect, incidental, special, consequential, or punitive damages
          arising out of or related to your use of the Service, even if Invitial has been advised of
          the possibility of such damages.
        </p>
        <p>
          Invitial's total aggregate liability to you for any direct losses arising out of or related
          to these Terms shall not exceed the amounts paid by your organisation for the Service in
          the twelve months preceding the claim.
        </p>

        <h6>9. Termination of Access</h6>
        <p>Invitial reserves the right to suspend or terminate your access to the Service:</p>
        <ul>
          <li>
            immediately, without notice, where Invitial reasonably believes there has been a serious
            breach of these Terms, a security incident, or unlawful activity;
          </li>
          <li>on not less than 30 days' written notice, for any other reason at Invitial's discretion.</li>
        </ul>
        <p>
          Upon termination, your right to access the Service ceases immediately. Invitial will, upon
          written request made within 30 days of termination, provide a reasonable means for your
          organisation to export or retrieve its content data held within the Service. After that
          period, Invitial may delete such data in accordance with its data retention practices.
        </p>

        <h6>10. Changes to These Terms</h6>
        <p>
          Invitial may update these Terms from time to time. Where changes are material, Invitial
          will provide at least 14 days' written notice (including by email) before the changes take
          effect. Continued use of the Service following the effective date of any changes constitutes
          your acceptance of the revised Terms. It is your responsibility to review these Terms
          periodically.
        </p>

        <h6>11. Force Majeure</h6>
        <p>
          Invitial shall not be liable for any failure or delay in performing its obligations under
          these Terms to the extent that such failure or delay is caused by circumstances beyond its
          reasonable control, including acts of God, natural disasters, war, civil unrest, government
          action, power failures, or internet outages. Invitial will notify you as soon as reasonably
          practicable of any such event and will use reasonable endeavours to resume performance
          promptly.
        </p>

        <h6>12. Governing Law and Disputes</h6>
        <p>
          These Terms are governed by and construed in accordance with the laws of England and Wales.
          In the event of any dispute arising under these Terms, the parties agree to attempt to
          resolve the matter through good faith negotiation before initiating formal proceedings. Any
          disputes that cannot be resolved by negotiation shall be subject to the exclusive
          jurisdiction of the courts of England and Wales.
        </p>

        <h6>13. Contact</h6>
        <p>
          If you have any questions about these Terms, please contact Invitial at
          <a href="mailto:info@ifedata.com">info@ifedata.com</a>.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'TermsOfServiceModal',
};
</script>

<style scoped>
.tos-content h6 {
  font-weight: 600;
  margin-top: 1.25rem;
}
</style>
